import React from "react";
import { useT } from "@/lib/LanguageContext";
import { TAG_MAX_LENGTH } from "../hqCustomerRules";
import { LIFECYCLE_STAGES, configFieldsOf, fieldProblem } from "./actionConfigRules";
import { configFieldLabel } from "./automationCodes";

const FIELD_LABEL = "block text-xs text-slate-500 mb-1";
const CONTROL = "w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm";
const FLAGGED_CONTROL = "border-rose-400 ring-1 ring-rose-300";
// The limit a field's rule sentence names (`hauto.cfg.rule.<field>`).
const ruleMax = (field) => (field.kind === "tag" ? TAG_MAX_LENGTH : field.max);

/**
 * What a flagged field says, from what is wrong with its current value: empty -> the
 * field's own request (`hauto.cfg.required.<field>`), too long -> the count and the limit,
 * anything else, or a server refusal the value no longer explains -> the field's rule.
 */
function flaggedMessage(t, field, value) {
  const problem = fieldProblem(field, value);
  if (problem?.code === "required") return t(`hauto.cfg.required.${field.name}`);
  if (problem?.code === "tooLong") {
    return t(field.kind === "slackChannel" ? "hauto.cfg.tooLong.channel" : "hauto.cfg.tooLong", { count: problem.length, max: problem.max });
  }
  return t(`hauto.cfg.rule.${field.name}`, { max: ruleMax(field) });
}

/**
 * The settings one automation action needs (actionConfigRules.ACTION_CONFIG_FIELDS), as a small
 * form. Controlled: `config` is the action's config object, `onChange(name, value)` reports one
 * field. `invalid` names the fields to flag (refused by the form, by the server, or listed in the
 * rule's `config_issues`); a flagged field is outlined and says what is wrong with it
 * (`flaggedMessage`). Every value is shown as text in an input, the email body (operator HTML,
 * sent as-is) included: nothing here renders it. Validation is this form's own: a rule form that
 * holds it sets `noValidate`, so the browser's bubble never speaks in place of these messages.
 *
 * `testIdPrefix` keeps two forms on one screen apart: `${prefix}-<field>`, `${prefix}-error-<field>`.
 */
export default function ActionConfigForm({ type, config, onChange, invalid = [], testIdPrefix = "cfg" }) {
  const t = useT("superadmin");
  const values = config && typeof config === "object" && !Array.isArray(config) ? config : {};
  const fields = configFieldsOf(type);
  if (fields.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="action-config-form">
      {fields.map((field) => {
        const id = `${testIdPrefix}-${field.name}`;
        const flagged = invalid.includes(field.name);
        const value = typeof values[field.name] === "string" ? values[field.name] : "";
        const common = {
          id, value, "data-testid": id, "aria-invalid": flagged, "aria-required": field.required,
          "aria-describedby": flagged ? `${id}-error` : undefined,
          onChange: (e) => onChange(field.name, e.target.value),
        };
        const controlClass = flagged ? `${CONTROL} ${FLAGGED_CONTROL}` : CONTROL;
        let control;
        if (field.kind === "stage") {
          control = (
            <select {...common} className={controlClass}>
              <option value="">—</option>
              {LIFECYCLE_STAGES.map((s) => <option key={s} value={s}>{t(`hc.stage.${s}`)}</option>)}
            </select>
          );
        } else if (field.kind === "text") {
          control = <textarea {...common} rows={field.name === "body" ? 5 : 3} className={controlClass} />;
        } else {
          control = (
            <input {...common} type={field.kind === "email" ? "email" : "text"} className={controlClass}
              placeholder={field.kind === "email" ? t("hauto.cfg.ph.to") : field.kind === "slackChannel" ? t("hauto.cfg.ph.channel") : undefined} />
          );
        }
        return (
          <div key={field.name}>
            <label htmlFor={id} className={FIELD_LABEL}>
              {field.required ? configFieldLabel(t, field.name) : t("hauto.cfg.optionalLabel", { label: configFieldLabel(t, field.name) })}
            </label>
            {control}
            {field.name === "body" && <div className="text-[11px] text-slate-400 mt-1">{t("hauto.cfg.bodyHint")}</div>}
            {flagged && (
              <div id={`${id}-error`} role="alert" className="text-xs text-rose-600 mt-1" data-testid={`${testIdPrefix}-error-${field.name}`}>
                {flaggedMessage(t, field, values[field.name])}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
